import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Drawer,
  Loader,
  Textarea,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconBuildingCommunity,
  IconChevronDown,
  IconCircleCheck,
  IconFileDescription,
  IconInfoCircle,
  IconSearch,
} from "@tabler/icons-react";
import { stateZipCodes } from "../../utils/zipCodes";
import { EHR_OPTIONS } from "../../utils/ehrOptions";
import {
  addExternalSkypointProvider,
  addSkypointProvider,
  getProviderSettings,
  requestProviderAccess,
  searchProviders,
} from "../../services/skypointProvider";
import { createProviderFolder } from "../../services/providerDocuments";
import useDebounce from "../../utils/useDebounce";
import useStyles from "./style";

const STATE_OPTIONS = Array.from(
  new Set(stateZipCodes?.map((obj) => obj.ste_name))
)
  .sort()
  .map((ste_name) => ({ value: ste_name, label: ste_name }));

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

// Providers are recorded inconsistently: some carry a businessName, some only
// the owner's name, some just first/last.
const providerNameOf = (p) =>
  p?.businessName || p?.name || `${p?.firstName || ""} ${p?.lastName || ""}`.trim() || "Provider";

// Matches the server's own minimum for a name search (see the /search-user
// route) — below this the search doesn't run at all.
const MIN_SEARCH_LENGTH = 3;

const emptyValues = {
  firstName: "",
  lastName: "",
  businessName: "",
  email: "",
  phone: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  zipcode: "",
  npi: "",
  ehrSystem: "",
  ehrSystemOther: "",
  providerNotes: "",
};

const AddProviderDrawer = ({ opened, onClose, onAdded }) => {
  const { classes, cx } = useStyles();
  const { providerSettings } = useSelector((state) => state.skypointProviders);
  const defaultKind = providerSettings?.defaultProviderType || "external";
  const ehrOptions = providerSettings?.ehrOptions?.length ? providerSettings.ehrOptions : EHR_OPTIONS;
  const requireEhr = providerSettings?.requireEhr !== false;
  const requireNpi = !!providerSettings?.requireNpi;
  const [providerKind, setProviderKind] = useState(defaultKind);
  const [ogisoStep, setOgisoStep] = useState("search");
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  // Whether a query has actually been run — separates "nothing found" from
  // "nothing searched for yet", which look identical from an empty result list.
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  // Which result row is mid-request, so only that row shows "Adding…".
  const [linkingId, setLinkingId] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);

  useEffect(() => {
    if (!providerSettings) getProviderSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    initialValues: emptyValues,
    validate: {
      firstName: (value) => (value ? null : "Required"),
      lastName: (value) => (value ? null : "Required"),
      businessName: (value) => (value ? null : "Required"),
      email: (value) => {
        if (!value) return "Required";
        return /^\S+@\S+$/.test(value) ? null : "Invalid email format";
      },
      phone: (value) => (value ? null : "Required"),
      address: (value) => (value ? null : "Required"),
      zipcode: (value) => (value ? null : "Required"),
      state: (value) => (value ? null : "Required"),
      npi: (value) => {
        if (!value) return requireNpi ? "Required" : null;
        return /^\d{10}$/.test(value) ? null : "NPI must be 10 digits";
      },
      ehrSystem: (value) =>
        providerKind === "external" && requireEhr && !value ? "Required" : null,
      ehrSystemOther: (value, values) =>
        providerKind === "external" && values.ehrSystem === "Other" && !value
          ? "Required"
          : null,
    },
  });

  const resetAll = () => {
    setProviderKind(defaultKind);
    setOgisoStep("search");
    setSearchTerm("");
    setSearchResults([]);
    setSearched(false);
    setLinkingId(null);
    setSentInfo(null);
    form.setValues(emptyValues);
    form.clearErrors();
  };

  useEffect(() => {
    if (opened) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultKind]);

  // Searches as the contractor types (debounced) rather than behind a Search
  // button: they are looking for a provider by name and may not know the exact
  // business name, so the list has to react while they narrow it. A full email
  // address is still matched exactly server-side.
  const debouncedSearch = useDebounce(searchTerm, 400);
  useEffect(() => {
    const term = debouncedSearch.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setSearched(false);
      // Clearing the box while a request is still in flight would otherwise
      // leave the spinner running with nothing left to resolve it.
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    searchProviders(term).then((results) => {
      // A response for an abandoned query must not replace the current one:
      // requests can resolve out of order.
      if (cancelled) return;
      setSearchResults(results);
      setSearching(false);
      setSearched(true);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const handleLinkProvider = async (result) => {
    if (!result) return;
    setLinkingId(result.id || result._id);
    const res = await requestProviderAccess(result.id || result._id);
    setLinkingId(null);
    if (res) setSentInfo({ name: providerNameOf(result), email: result.email });
  };


  // The server rejects an email that already belongs to a provider. Flag the
  // input so the message sits next to the field that has to change, and keep
  // the drawer open on the entered values instead of a toast the user reads
  // after the form has already scrolled away.
  const showFieldError = (field, message) => {
    if (!(field in form.values)) return false;
    form.setFieldError(field, message || "Already in use");
    return true;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const { email, ehrSystem, ehrSystemOther, ...rest } = values;
      const resolvedEhrSystem =
        ehrSystem === "Other" ? ehrSystemOther?.trim() : ehrSystem;
      const name = `${values.firstName} ${values.lastName}`.trim();

      if (providerKind === "external") {
        const payload = { ...values, ehrSystem: resolvedEhrSystem, name };
        delete payload.ehrSystemOther;
        const res = await addExternalSkypointProvider(payload, showFieldError);
        if (res) {
          const newProviderId = res?.user?._id || res?.user?.id;
          const defaultFolders = providerSettings?.defaultFolders || [];
          if (newProviderId && defaultFolders.length) {
            for (const folderName of defaultFolders) {
              await createProviderFolder(newProviderId, folderName);
            }
          }
          handleClose();
          onAdded?.();
        }
      } else {
        const payload = { ...rest, email, name, platform: "ogiso" };
        const res = await addSkypointProvider(payload, showFieldError);
        if (res) {
          setSentInfo({ name: values.businessName || name, email });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const pickKind = (kind) => {
    setProviderKind(kind);
    setOgisoStep("search");
  };

  const backFromCreate = () => {
    setOgisoStep("search");
  };

  const isCreatingOgiso = providerKind === "ogiso" && ogisoStep === "create";
  const showChoiceStep = !sentInfo;
  const primaryLabel = isCreatingOgiso ? "Add Provider" : "Add External Provider";

  const handlePrimary = () => {
    if (sentInfo) {
      handleClose();
      onAdded?.();
      return;
    }
    if (providerKind === "ogiso" && ogisoStep === "search") return; // linking happens per-result
    const validation = form.validate();
    if (!validation.hasErrors) {
      handleSubmit(form.values);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size={620}
      withCloseButton={false}
      shadow="md"
      overlayProps={{ opacity: 0.55, blur: 2 }}
      // The drawer holds a long form, and any sweetalert raised from inside it
      // (a duplicate email, a failed request) paints outside the drawer — so a
      // stray click, including one dismissing that alert, must not throw the
      // work away. Closing is explicit: Cancel or the ✕.
      closeOnClickOutside={false}
      className={classes.drawer}
      zIndex={1000}
    >
      <Box className={classes.head}>
        <Box>
          <Text className={classes.headTitle}>
            {sentInfo
              ? "Invitation Sent"
              : isCreatingOgiso
              ? "Create Ogiso Provider"
              : "Add Provider"}
          </Text>
          {!sentInfo && !isCreatingOgiso && (
            <Text className={classes.headSub}>
              Choose how this provider works with your agency.
            </Text>
          )}
          {isCreatingOgiso && (
            <Text className={classes.headSub}>
              Enter the provider&rsquo;s details. An invitation email will be sent.
            </Text>
          )}
        </Box>
        <ActionCloseButton classes={classes} onClick={handleClose} />
      </Box>

      <Box className={classes.body}>
        {sentInfo ? (
          <Box className={classes.sentWrap}>
            <Box className={classes.sentIcon}>
              <IconCircleCheck size={44} stroke={1.6} />
            </Box>
            <Text className={classes.sentTitle}>Invitation sent</Text>
            <Text className={classes.sentSub}>
              We emailed an invitation to <strong>{sentInfo.email}</strong>. They&rsquo;ll
              appear in your provider list once they accept.
            </Text>
            <Box className={classes.sentCard}>
              <Box className={classes.sentAv}>{initialsOf(sentInfo.name)}</Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text className={classes.sentName}>{sentInfo.name}</Text>
                <Text className={classes.sentMail}>
                  <span className={classes.sentPill}>Ogiso</span>
                  <span>Pending acceptance</span>
                </Text>
              </Box>
            </Box>

            {/* Sending the invitation is only the first of several steps before
                this provider's notes can feed an AI quarterly report, and the
                remaining ones are theirs to complete — spelling them out here
                is what stops the provider list looking broken while they are
                still working through onboarding. */}
            <Box className={classes.sentNext}>
              <Text className={classes.sentNextLbl}>What happens next</Text>
              {[
                "They accept the invitation and set up their Ogiso login.",
                "They select a package and complete checkout.",
                "They add their staff members and facilities.",
                "Their staff start documenting in Ogiso, and those notes power AI quarterly reports.",
              ].map((step, i) => (
                <Box key={i} className={classes.sentStep}>
                  <span className={classes.sentStepNum}>{i + 1}</span>
                  <span>{step}</span>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {!isCreatingOgiso && (
              <Box className={classes.section}>
                <Text className={classes.sectionLabel}>Provider type</Text>
                <Box className={classes.choiceGrid}>
                  {[
                    { kind: "ogiso", label: "Ogiso provider", icon: IconBuildingCommunity },
                    { kind: "external", label: "External provider", icon: IconFileDescription },
                  ].map(({ kind, label, icon: Icon }) => (
                    <button
                      type="button"
                      key={kind}
                      className={cx(classes.choiceCard, {
                        [classes.choiceCardOn]: providerKind === kind,
                      })}
                      onClick={() => pickKind(kind)}
                    >
                      <Box className={classes.choiceTop}>
                        <Box
                          className={cx(classes.choiceIcn, {
                            [classes.choiceIcnOn]: providerKind === kind,
                          })}
                        >
                          <Icon size={19} stroke={1.9} />
                        </Box>
                        <Box
                          className={cx(classes.choiceRadio, {
                            [classes.choiceRadioOn]: providerKind === kind,
                          })}
                        />
                      </Box>
                      <Text className={classes.choiceTitle}>{label}</Text>
                    </button>
                  ))}
                </Box>
              </Box>
            )}

            {providerKind === "external" && (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Box className={classes.section}>
                  <Text className={classes.sectionLabel}>Provider details</Text>
                  <Box className={classes.formGrid}>
                    <TextInput
                      className={classes.field}
                      label="Business Name"
                      placeholder="e.g. Lakeview Residential"
                      {...form.getInputProps("businessName")}
                    />
                    <Select
                      className={classes.field}
                      label="EHR system"
                      placeholder="Select EHR"
                      rightSection={<IconChevronDown size={16} color="#9CA3AF" />}
                      data={ehrOptions.map((ehr) => ({ value: ehr, label: ehr }))}
                      {...form.getInputProps("ehrSystem")}
                    />
                    {form.values.ehrSystem === "Other" && (
                      <TextInput
                        className={classes.field}
                        label="EHR Name"
                        placeholder="Enter EHR system name"
                        {...form.getInputProps("ehrSystemOther")}
                      />
                    )}
                    <TextInput
                      className={classes.field}
                      label="Contact First Name"
                      placeholder="Enter first name"
                      {...form.getInputProps("firstName")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Contact Last Name"
                      placeholder="Enter last name"
                      {...form.getInputProps("lastName")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Contact Email"
                      placeholder="name@provider.org"
                      {...form.getInputProps("email")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Contact Phone"
                      placeholder="(000) 000-0000"
                      {...form.getInputProps("phone")}
                    />
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="Address 1"
                        placeholder="Enter address 1"
                        {...form.getInputProps("address")}
                      />
                    </Box>
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="Address 2"
                        placeholder="Enter address 2 (optional)"
                        {...form.getInputProps("address2")}
                      />
                    </Box>
                    <TextInput
                      className={classes.field}
                      label="City"
                      placeholder="Enter city"
                      {...form.getInputProps("city")}
                    />
                    <Select
                      className={classes.field}
                      label="State"
                      placeholder="Select state"
                      rightSection={<IconChevronDown size={16} color="#9CA3AF" />}
                      searchable
                      data={STATE_OPTIONS}
                      {...form.getInputProps("state")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Zipcode"
                      placeholder="Enter zipcode"
                      {...form.getInputProps("zipcode")}
                    />
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="NPI # (National Provider Identifier)"
                        placeholder="Enter 10-digit NPI number"
                        {...form.getInputProps("npi")}
                      />
                    </Box>
                    <Box className={cx(classes.field, classes.span2)}>
                      <Textarea
                        label="Notes"
                        placeholder="Anything useful about this provider…"
                        minRows={3}
                        autosize
                        {...form.getInputProps("providerNotes")}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.hint}>
                  <IconInfoCircle size={16} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    This provider won&rsquo;t have login access. No invitation email is sent,
                    and they won&rsquo;t appear in staff, auth, or notification lists.
                  </span>
                </Box>
              </form>
            )}

            {providerKind === "ogiso" && ogisoStep === "search" && (
              <Box className={classes.section}>
                <Text className={classes.sectionLabel}>Find the Ogiso provider</Text>
                <Box className={classes.psearch}>
                  <IconSearch size={16} stroke={2} />
                  <input
                    placeholder="Search by provider name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searching && <Loader size={15} color="violet" />}
                </Box>
                <Text
                  style={{
                    margin: "10px 2px 12px",
                    fontSize: 12.5,
                    color: "#667085",
                    fontFamily: "Inter",
                  }}
                >
                  Providers already on Ogiso. Select one to link them to your agency and
                  send an access request.
                </Text>
                {searchResults.length > 0 && (
                  <Box className={classes.presultList}>
                    {searchResults.map((result) => {
                      const id = result.id || result._id;
                      const place = [result.city, result.state].filter(Boolean).join(", ");
                      return (
                        <button
                          type="button"
                          key={id}
                          className={classes.presult}
                          disabled={!!linkingId}
                          onClick={() => handleLinkProvider(result)}
                        >
                          <Box className={classes.pav}>{initialsOf(providerNameOf(result))}</Box>
                          <Box style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                            <Text className={classes.pname}>{providerNameOf(result)}</Text>
                            <Text className={classes.pmeta}>
                              {[result.email, place].filter(Boolean).join(" · ")}
                            </Text>
                          </Box>
                          <Text className={classes.paddLink}>
                            {linkingId === id ? "Adding…" : "Add"}
                          </Text>
                        </button>
                      );
                    })}
                  </Box>
                )}
                {searched && !searching && searchResults.length === 0 && (
                  <Box className={classes.pempty}>
                    No provider found matching that search. Check the spelling, or create a
                    new Ogiso provider below.
                  </Box>
                )}
                <button type="button" className={classes.addBtnDashed} onClick={() => setOgisoStep("create")}>
                  Can&rsquo;t find them? Create a new Ogiso provider
                </button>
              </Box>
            )}

            {isCreatingOgiso && (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Box className={classes.section}>
                  <Text className={classes.sectionLabel}>Create a new Ogiso provider</Text>
                  <Box className={classes.formGrid}>
                    <TextInput
                      className={classes.field}
                      label="Admin First Name"
                      placeholder="Enter first name"
                      {...form.getInputProps("firstName")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Admin Last Name"
                      placeholder="Enter last name"
                      {...form.getInputProps("lastName")}
                    />
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="Business Name"
                        placeholder="Enter business name"
                        {...form.getInputProps("businessName")}
                      />
                    </Box>
                    <TextInput
                      className={classes.field}
                      label="Email"
                      placeholder="Enter email"
                      {...form.getInputProps("email")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Phone Number"
                      placeholder="Enter phone number"
                      {...form.getInputProps("phone")}
                    />
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="Address 1"
                        placeholder="Enter address 1"
                        {...form.getInputProps("address")}
                      />
                    </Box>
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="Address 2"
                        placeholder="Enter address 2 (optional)"
                        {...form.getInputProps("address2")}
                      />
                    </Box>
                    <TextInput
                      className={classes.field}
                      label="City"
                      placeholder="Enter city"
                      {...form.getInputProps("city")}
                    />
                    <Select
                      className={classes.field}
                      label="State"
                      placeholder="Select state"
                      rightSection={<IconChevronDown size={16} color="#9CA3AF" />}
                      searchable
                      data={STATE_OPTIONS}
                      {...form.getInputProps("state")}
                    />
                    <TextInput
                      className={classes.field}
                      label="Zipcode"
                      placeholder="Enter zipcode"
                      {...form.getInputProps("zipcode")}
                    />
                    <Box className={cx(classes.field, classes.span2)}>
                      <TextInput
                        label="NPI # (National Provider Identifier)"
                        placeholder="Enter 10-digit NPI number"
                        {...form.getInputProps("npi")}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.hint}>
                  <IconInfoCircle size={16} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    An invitation email is sent so the provider can set up their Ogiso
                    login and dashboard.
                  </span>
                </Box>
              </form>
            )}
          </>
        )}
      </Box>

      <Box className={classes.foot}>
        <button
          type="button"
          className={classes.btnGhost}
          onClick={sentInfo ? resetAll : isCreatingOgiso ? backFromCreate : handleClose}
        >
          {sentInfo ? "Add another provider" : isCreatingOgiso ? "Back" : "Cancel"}
        </button>
        {(sentInfo || providerKind === "external" || isCreatingOgiso) && (
          <button
            type="button"
            className={classes.btnAdd}
            disabled={loading}
            onClick={handlePrimary}
          >
            {sentInfo ? "Done" : loading ? "Saving…" : primaryLabel}
          </button>
        )}
      </Box>
    </Drawer>
  );
};

const ActionCloseButton = ({ classes, onClick }) => (
  <button type="button" className={classes.closeBtn} onClick={onClick} aria-label="Close">
    &times;
  </button>
);

export default AddProviderDrawer;
